import styled from 'styled-components';
import { Show } from '@elfsight/lib-utils';
import { useSettings } from '@wu/widget-core';
import { createPortal } from 'react-dom';
import type { Position } from '../../../../../types';
import { positionAdjust, positionCheck } from '../../../../helpers';
import { Deactivation, useTheme } from '../../../common';
import { usePlayer } from '../../PlayerProvider';
import { Duration } from '../../playlist';
import { Small } from '../widget-types';

export function FloatingSmall() {
  const { textColor, backgroundStyle } = useTheme();
  const { showPlayer } = usePlayer();

  const {
    font,
    position,
    backgroundColor = '',
    widgetWidth,
    displayFreeLink,
    displayDeactivation = false,
    layout
  } = useSettings();

  const smallProps = {
    isFloating: true,
    _backgroundStyle: backgroundStyle,
    _hasFreeLinkPadding: displayFreeLink && layout === 'floating'
  };

  return createPortal(
    <Container
      _color={textColor}
      _position={position}
      _backgroundColor={backgroundColor}
      _widgetWidth={widgetWidth.toString()}
      _font={font?.value || 'inherit'}
    >
      <Show
        when={displayDeactivation && showPlayer}
        fallback={<StyledSmall {...smallProps} />}
      >
        <Deactivation containerStyle={{ width: '100%' }}>
          <DeactivationInnerWrapper>
            <StyledSmall {...smallProps} />
          </DeactivationInnerWrapper>
        </Deactivation>
      </Show>
    </Container>,
    document.body
  );
}

const Container = styled.div<{
  _color: string;
  _position: Position;
  _backgroundColor: string;
  _widgetWidth: string | number;
  _font: string;
}>`
  position: fixed;
  width: calc(100% - 24px);
  display: flex;
  align-items: center;
  z-index: 999999999;
  border-radius: 8px;

  max-width: ${({ _widgetWidth }) =>
    _widgetWidth.toString().includes('%') ? _widgetWidth : `${_widgetWidth}px`};
  ${({ _position }) => `
    flex-direction: ${
      positionCheck(_position, 'top') ? 'column' : 'column-reverse'
    };
    top: ${positionAdjust(_position, 'top')};
    bottom: ${positionAdjust(_position, 'bottom')};
    left: ${positionAdjust(_position, 'left')};
    right: ${positionAdjust(_position, 'right')};
  `};
  color: ${({ _color }) => _color};
  font-family: ${({ _font }) => _font};

  & ${Duration} {
    margin-left: auto;
  }
`;

const DeactivationInnerWrapper = styled.div`
  width: 100%;
`;

const StyledSmall = styled(Small)<{
  _backgroundStyle?: string;
}>`
  border-radius: 8px;
  width: 100%;
  ${({ _backgroundStyle }) => _backgroundStyle}
`;
